import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useStyles } from './styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

function CustomTimePicker<TFieldValues extends FieldValues = FieldValues>({
  title,
  field,
  control,
  enabled,
  day_of_week,
  defaultValue,
  onDataChange,
  compact,
  disableTimezone,
}: {
  title: string;
  field: string;
  control: Control<TFieldValues>;
  enabled: boolean;
  day_of_week: string;
  defaultValue: string;
  onDataChange: (dayOfWeek: string, data: any) => void;
  compact?: boolean;
  disableTimezone?: boolean;
}) {
  const classes = useStyles();
  const formattedDefaultValue = useMemo(() => {
    if (defaultValue) {
      const parsedTime = dayjs(defaultValue);
      if (disableTimezone) {
        return dayjs(parsedTime.format('YYYY-MM-DDTHH:mm'));
      }
      return dayjs(parsedTime.format('YYYY-MM-DDTHH:mm')).tz('Asia/Tokyo');
    }
    return null;
  }, [defaultValue, disableTimezone]);

  const [open, setOpen] = useState(false);

  const handleTimeChange = (value: any) => {
    if (disableTimezone) {
      const hour = dayjs(value).hour();
      const minute = dayjs(value).minute();
      const newDate = dayjs(formattedDefaultValue).hour(hour).minute(minute);
      onDataChange(day_of_week, { [field]: newDate.format('YYYY-MM-DDTHH:mm:ss') });
    } else {
      const formattedTime = dayjs(value).tz('Asia/Tokyo');
      const hour = formattedTime.hour();
      const minute = formattedTime.minute();
      const newDate = dayjs(formattedDefaultValue).hour(hour).minute(minute).tz('Asia/Tokyo');
      onDataChange(day_of_week, { [field]: newDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ') });
    }
  };

  return (
    <Box className={classes.TimePicker}>
      <Box className={classes.Title}>
        <Typography color={enabled ? '#667085' : 'rgb(204, 204, 204)'} fontSize={16} fontWeight={enabled ? 400 : 600} lineHeight={'24px'} whiteSpace={'nowrap'}>
          {title}
        </Typography>
      </Box>
      <Box className={classes.Picker} sx={compact ? { width: '110px', flexBasis: '110px' } : {}}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Controller
            name={`${day_of_week.toLowerCase()}_${title.toLowerCase()}` as Path<TFieldValues>}
            control={control}
            render={({ field }) => (
              <TimePicker
                {...field}
                views={['hours', 'minutes']}
                format="HH:mm"
                ampm={false}
                disabled={!enabled}
                defaultValue={formattedDefaultValue}
                onChange={handleTimeChange}
                timeSteps={{ hours: 1, minutes: 15 }}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                slotProps={{
                  textField: {
                    onClick: () => setOpen(true),
                    sx: compact ? {
                      '& .MuiInputBase-root': {
                        height: '40px',
                        padding: '0 8px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '0 8px',
                      },
                    } : {},
                  },
                }}
              />
            )}
          />
        </LocalizationProvider>
      </Box>
    </Box>
  );
}

export default CustomTimePicker;
